// Help page: keyboard shortcuts, icon glossary, and quick how-tos.
// Linked from the top nav so it is reachable from anywhere.
// Icons are rendered inline via <Icon> — no emoji.

import { Icon } from "../ui/icons";
import { Card } from "../ui/Card";

export function Help() {
  return (
    <>
      <h2>Help</h2>

      {/* ---- Keyboard shortcuts ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Keyboard shortcuts</h3>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
          <tbody>
            <Shortcut keys="Cmd / Ctrl + Z" what="Undo the last action. Can be chained backward; works across sessions." />
            <Shortcut keys="Alt (hold)" what="Peek the raw transcript before your edits. Release to return to your edited view. The Option key on Mac." />
            <Shortcut keys="Esc" what="Inside edit mode: exits and keeps your changes. Inside a popover: cancels just that popover." />
            <Shortcut keys="Enter" what="Inside speaker, split, or rename popovers: save and close." />
          </tbody>
        </table>
      </Card>

      {/* ---- What the buttons mean ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>What the buttons mean</h3>

        <IconEntry icon={<Icon name="edit" size={15} />} label="Edit (pencil) at the end of a paragraph">
          Opens the paragraph in edit mode so you can fix text. The{" "}
          <InlineIcon><Icon name="scissors" size={14} /></InlineIcon> (split) and{" "}
          <InlineIcon><Icon name="bookmark" size={14} /></InlineIcon> (bookmark) icons
          next to it act without entering edit mode.
        </IconEntry>

        <IconEntry icon={<Icon name="edit" size={15} />} label="Edit (pencil) next to a speaker name">
          Click to rename the speaker for this turn. Choose{" "}
          &quot;Whole meeting&quot; to rename everywhere this voice appears, or{" "}
          &quot;Just this part&quot; to cover only this stretch.
        </IconEntry>

        <IconEntry icon={<Icon name="bookmark" size={15} />} label="Bookmark in the page margin">
          A bookmark you placed on this paragraph. Click it to jump audio to that
          paragraph. You can bookmark as many paragraphs as you like — each bookmark
          stays until you click it again to remove it. Bookmarks are visible only in the
          app; they never appear in exports.
        </IconEntry>

        <IconEntry icon={<span style={{ display: "inline-block", width: 3, alignSelf: "stretch", background: "var(--ink-3)", borderRadius: 2 }} />} label="Dashed line down the left edge of a paragraph">
          You touched this paragraph (text correction, speaker override, or split). Lets
          you scan the transcript and see which paragraphs you have worked on.
        </IconEntry>

        <IconEntry icon={<span style={{ fontSize: 12, borderBottom: "1px dotted var(--ink-2)" }}>word</span>} label="Dotted underline on text">
          That text is a correction you made. Hover to see the original wording. Click
          the small close button to undo the correction.
        </IconEntry>

        <IconEntry icon={<Icon name="eye" size={15} />} label="Hide my edits / Show my edits">
          Toggles between your edited view and the raw transcript as it arrived. Helpful
          when you cannot remember what the original said. Holding Alt does the same
          thing temporarily without clicking.
        </IconEntry>

        <IconEntry icon={<Icon name="undo" size={15} />} label="Undo">
          Reverses the last action. If you exited an edit session with several changes,
          one click reverses the whole session in one go. Can be chained backward across
          sessions.
        </IconEntry>

        <IconEntry icon={<Icon name="unlock" size={15} />} label="Lock / Unlock">
          Locking marks the transcript as final: editing affordances hide and export
          buttons appear. Unlock to resume editing.
        </IconEntry>

        <IconEntry icon={<Icon name="refresh" size={15} />} label="Update the analysis">
          Re-runs the full analysis on the existing transcript — who said what (with an
          independent second check), suspicious wording, names &amp; terms, and a fresh
          summary. Audio and verbatim text are not touched.
        </IconEntry>

        <IconEntry icon={<Icon name="undo" size={15} />} label="Reset to original (inside edit mode)">
          Removes all your changes to a single paragraph: corrections, speaker overrides,
          splits, and the bookmark on that paragraph. A confirmation dialog lists what
          will be removed. Reversible via the Undo button afterward.
        </IconEntry>

        <IconEntry icon={<Icon name="x" size={15} />} label="Cancel (inside edit mode)">
          Reverses every change you made in this edit session in one shot. This is the
          only action that loses session work. Click-outside and Escape both behave as
          Save, so accidental clicks will not cost you.
        </IconEntry>
      </Card>

      {/* ---- How to split a paragraph ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>How to split a paragraph</h3>
        <ol style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li>
            Hover over the paragraph and click the{" "}
            <InlineIcon><Icon name="scissors" size={13} /></InlineIcon> split icon.
          </li>
          <li>Click the word where the new speaker takes over (a hint bar shows while picking).</li>
          <li>Type the speaker name for the second half and click Save.</li>
          <li>
            The paragraph splits visually. You can split again to make three paragraphs — useful when one
            paragraph contains a quick back-and-forth between speakers.
          </li>
        </ol>
      </Card>

      {/* ---- How to fix a wrong word ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>How to fix a wrong word</h3>
        <ol style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li>
            Open edit mode (click the{" "}
            <InlineIcon><Icon name="edit" size={13} /></InlineIcon> icon at the end of the paragraph).
          </li>
          <li>Select the word or phrase with your mouse.</li>
          <li>A correction popover opens. Type the replacement and click Save.</li>
          <li>The corrected text shows with a dotted underline. Hover to see the original.</li>
        </ol>
      </Card>

      {/* ---- Recovery ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>Recovery if you make a mistake</h3>
        <ul style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li>
            <strong>Single action wrong:</strong> click{" "}
            <InlineIcon><Icon name="undo" size={13} /></InlineIcon>{" "}
            <strong>Undo</strong> in the top toolbar. Keep clicking to chain backward.
          </li>
          <li>
            <strong>Whole edit session wrong:</strong> while still in edit mode, click{" "}
            <strong>Cancel</strong>. Reverses everything you did this session in one shot.
          </li>
          <li>
            <strong>Want to start a paragraph over:</strong> open edit mode, click{" "}
            <strong>Reset to original</strong>. Removes all your changes to that paragraph.
            Reversible via Undo if you change your mind.
          </li>
          <li>
            <strong>Forgot what the original looked like:</strong> hold Alt or click{" "}
            <InlineIcon><Icon name="eye" size={13} /></InlineIcon>{" "}
            <strong>Hide my edits</strong>. Compare without scrolling.
          </li>
        </ul>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Shortcut({ keys, what }: { keys: string; what: string }) {
  return (
    <tr>
      <td
        style={{
          padding: "8px 12px 8px 0",
          whiteSpace: "nowrap",
          verticalAlign: "top",
          borderBottom: "1px solid var(--line)",
          width: 1,
        }}
      >
        <kbd
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            fontWeight: 600,
            background: "var(--surface-2)",
            border: "1px solid var(--line-2)",
            borderRadius: 4,
            padding: "2px 6px",
            color: "var(--ink-2)",
          }}
        >
          {keys}
        </kbd>
      </td>
      <td
        style={{
          padding: "8px 0",
          color: "var(--ink)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        {what}
      </td>
    </tr>
  );
}

function IconEntry({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <span
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--surface-2)",
          border: "1px solid var(--line)",
          borderRadius: "var(--radius-sm)",
          color: "var(--ink-2)",
          marginTop: 1,
        }}
      >
        {icon}
      </span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)", marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function InlineIcon({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        marginInline: 2,
        color: "var(--ink-2)",
      }}
    >
      {children}
    </span>
  );
}
